import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {DatePipe, formatDate, registerLocaleData} from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import {BaseClientComponent} from '../../../../components/base-client.component';
import {Dad} from '../../../models/dad';
import {saveAs} from 'file-saver';
import {UtilsService} from '../../../../appSharedModule/services/utils.service';
import {DadService} from '../../../services/dad.service';
import {TableService} from '../../../../services/table.service';
import {AuthenticationService} from '../../../../authenticationModule/services/authentication.service';
import {HttpClient} from '@angular/common/http';
import {DadSearchCriteria} from '../../../models/dadSearchCriteria';
import * as _ from 'lodash';
import {ActivatedRoute} from '@angular/router';
import {Table} from 'primeng/table';
import {DateTime} from 'luxon';

registerLocaleData(localeFr);

@Component({
  selector: 'app-dad-forms-list',
  templateUrl: './dad-forms-list.component.html',
  styleUrls: ['./dad-forms-list.component.scss']
})
export class DadFormsListComponent extends BaseClientComponent implements OnInit {
  @ViewChild('dataTable') dataTable: Table;
  @Input() exchangesNames: any[];
  @Input() exchangeNamesList: any[];
  @Output() parentAvailableCompanyNames: EventEmitter<any> = new EventEmitter<any>();

  dadsList: Dad[] = [];
  filteredDads: Dad[] = [];
  selectedDads = [];
  availableCompanyNames: any[] = [];
  loadError = false;
  dadSearchCriteria: DadSearchCriteria;
  dateFilterFrom: Date;
  dateFilterTo: Date;
  userChoicesFiltersDefaultDadAdmin = {
    companyOrBillingAccount: '',
    fromUsageMonth: null,
    toUsageMonth: null,
    exchanges: [],
    status: []
  };
  userChoicesActiveFilters = _.clone(this.userChoicesFiltersDefaultDadAdmin);

  cols = [
    {field: 'createdAt', header: 'Date', type: 'string', sortable: true, width: '10%'},
    {field: 'companyName', header: 'Company', type: 'string', sortable: true, width: '15%'},
    {field: 'billingAccountName', header: 'Billing account', type: 'string', sortable: true, width: '16%'},
    {field: 'exchangeNameInTitle', header: 'Exchange', type: 'string', sortable: true, width: '8%'},
    {field: 'status', header: 'Form status', sortable: false, type: 'string', width: '10%'},
    {field: 'usageMonth', header: 'Usage month', sortable: true, type: 'string', width: '9%'},
    {field: 'cutoffTime', header: 'Cutoff (UTC)', sortable: true, type: 'string', width: '12%'},
    {field: 'autoReport', header: 'Auto report', type: 'string', sortable: false, width: '9%'},
    {field: 'carriedForward', header: 'Carry forward', type: 'string', sortable: false, width: '9%'},
    {field: 'reminderStatus', header: 'Reminder status', type: 'string', sortable: false, width: '10%'},
    {field: 'action', header: 'Action', type: 'string', sortable: false, width: '50%'}
  ];

  possibleStatus = ['Draft', 'Quincy only', 'Reported', 'Saved', 'Passed Due', 'Expired'];
  statusOptions: { label: string; value: string }[];
  boxUrlDialogVisible = false;
  currentBoxDad: Dad = null;
  currentBoxUrl = null;
  boxUrlError = false;
  success = false;
  successMessage = '';

  constructor(protected utilsService: UtilsService,
              private dadsService: DadService,
              public tableService: TableService,
              private route: ActivatedRoute,
              private datePipe: DatePipe,
              public authenticationService: AuthenticationService,
              protected http: HttpClient) {
    super();
  }

  ngOnInit() {
    this.userChoicesActiveFilters = this.utilsService.loadUserPref('dadAdminFilter', this.userChoicesFiltersDefaultDadAdmin);
    this.dadSearchCriteria = new DadSearchCriteria(this.userChoicesActiveFilters);
    this.statusOptions = this.possibleStatus.map(x => ({
      label: x,
      value: x
    }));
    this.initializeDateFilter(this.userChoicesActiveFilters);
    const response$ = this.authenticationService.waitForAuthenticationThenLaunch(() => {
      this.getDadsList();
    });
    this.subscriptions.push(response$);
  }

  private initializeDateFilter(userChoicesActiveFilters) {
    if (userChoicesActiveFilters.fromUsageMonth) {
      this.dateFilterFrom = this.toLocalDate(userChoicesActiveFilters.fromUsageMonth);
      this.dateFilterFrom.setDate(1);
    } else {
      this.dateFilterFrom = new Date();
      this.dateFilterFrom.setDate(1);
      this.dateFilterFrom.setMonth(this.dateFilterFrom.getMonth() - 1);
    }
    this.dateFilterFrom.setDate(1);
    if (userChoicesActiveFilters.toUsageMonth) {
      this.dateFilterTo = this.toLocalDate(userChoicesActiveFilters.toUsageMonth);
    } else {
      this.dateFilterTo = new Date();
    }
    this.dateFilterTo.setDate(1);
  }

  private toLocalDate(value: string | Date): Date {
    const formatted = this.datePipe.transform(value, 'yyyy-MM-dd');
    const [year, month, day] = formatted.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private getExclusiveUpperBound(monthStart: Date): Date {
    const upperBound = new Date(monthStart);
    upperBound.setDate(2);
    return upperBound;
  }

  getDadsList() {
    this.dadsService.getDads(this.dateFilterFrom, this.getExclusiveUpperBound(this.dateFilterTo)).subscribe({
        next: res => {
          this.loadError = false;
          this.dadsList = res.filter(dad => dad.isExempt === false);
          this.filterDadsByCompany();
          this.utilsService.unlockUi();
          this.availableCompanyNames = this.dadsList.map(dad => {
            const cutoff = this.getCutoffDate(dad);
            dad.cutoff = cutoff ? cutoff.toFormat('dd/MM/yyyy HH:mm') : '';
            dad.cutoffTime = cutoff ? cutoff.toMillis() : null;
            return ({'label': dad.companyName, 'value': dad.companyName});
          });
          this.parentAvailableCompanyNames.emit(this.availableCompanyNames);
          this.setFilteredDadsList();
        }, error: () => {
          this.loadError = true;
          this.dadsList = [];
          this.filteredDads = [];
          this.utilsService.displayMessage('The DAD forms could not be loaded', 'Please try again later.', 'error');
        }
      }
    );
  }

  setFilteredDadsList() {
    const exchangeFilter = [];
    for (const exchange of this.exchangesNames) {
      if (this.dadSearchCriteria.exchanges.includes(exchange)) {
        exchangeFilter.push(exchange);
      }
    }
    const statusFilter = [];
    for (const status of this.possibleStatus) {
      if (this.dadSearchCriteria.status.includes(status)) {
        statusFilter.push(status);
      }
    }
    this.utilsService.saveUserData('dadAdminFilter',
      {
        'companyOrBillingAccount': this.dadSearchCriteria.companyOrBillingAccount,
        'fromUsageMonth': this.dateFilterFrom,
        'toUsageMonth': this.dateFilterTo,
        'exchanges': exchangeFilter,
        'status': statusFilter
      });
    const filteredDadsList = [];
    for (const dad of this.dadsList) {
      if (this.filterDads(dad)) {
        filteredDadsList.push(dad);
      }
    }
    this.filteredDads = filteredDadsList;
    this.dataTable.reset();
  }

  private filterDadsByCompany() {
    this.route.paramMap.subscribe(params => {
      if (params.get('companyUniqid') == null) {
        return;
      }
      this.dadSearchCriteria.companyOrBillingAccount = params.get('companyUniqid');
    });
  }

  canEditForm(dad: Dad) {
    if (dad.isSelfReported) {
      return false;
    }
    if (this.authenticationService.userCurrent.roles.includes('ROLE_MCKAY')) {
      return dad.status?.toLowerCase() !== 'reported';
    }
    return ['draft', 'expired', 'passed due'].includes(dad.status?.toLowerCase());
  }

  canSeeOriginalClientDeclaration(dad: Dad) {
    return dad.hasOriginalClientDeclaration && this.authenticationService.userCurrent.permissions.isMckayUser;
  }

  downloadPdf(dad: Dad) {
    this.dadsService.downloadPdf(dad.uniqid).subscribe({
        next: res => {
          this.downLoadFile(res.body, res.headers.get('link').split('.pdf')[0] + '.pdf');
        },
        error: () => {
          this.utilsService.displayMessage('The PDF could not be downloaded', 'Please try again later.', 'error');
        }
      }
    );
  }

  exportDadFormPdfs() {
    const dadsUniqIds = [];
    for (const dad of this.selectedDads) {
      dadsUniqIds.push(dad.uniqid);
    }
    this.dadsService.exportDadFormPdfs(dadsUniqIds).subscribe({
      next: res => {
        this.downLoadFile(res, formatDate(new Date(), 'yyyy-MM-dd', 'en') + '-dad-forms.zip');
      },
      error: () => {
        this.utilsService.displayMessage('The PDFs could not be exported', 'Please try again later.', 'error');
      }
    });
  }

  alterAutoReport(dad: Dad) {
    this.dadsService.alterAutoReport(dad.uniqid).subscribe(
      () => {
        this.success = true;
        this.successMessage = dad.autoReport ? 'Auto report has been disabled.' : 'Auto report has been enabled.';
        dad.autoReport = !dad.autoReport;
        window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
      }
    );
  }

  downLoadFile(data: any, fileName: string) {
    const blob = new Blob([data]);
    saveAs(blob, fileName);
  }

  filterDads(dad: Dad): boolean {
    return this.hasCompanyNameOrBillingAccount(dad) && this.hasStatus(dad) && this.hasExchangeName(dad);
  }

  hasCompanyNameOrBillingAccount(dad: Dad): boolean {
    const companyOrBillingAccount = this.dadSearchCriteria.companyOrBillingAccount;
    return companyOrBillingAccount === ''
      || dad.companyName.toLowerCase().includes(companyOrBillingAccount.toLowerCase())
      || dad.companyUniqid.toLowerCase().includes(companyOrBillingAccount.toLowerCase())
      || dad.billingAccountName.toLowerCase().includes(companyOrBillingAccount.toLowerCase());
  }

  hasExchangeName(dad: Dad): boolean {
    const checkedExchanges = [];
    for (const exchange of this.exchangesNames) {
      if (this.dadSearchCriteria.exchanges.includes(exchange)) {
        checkedExchanges.push(exchange.toLocaleLowerCase());
      }
    }
    return checkedExchanges.length === 0 || checkedExchanges.includes(dad.exchangeNameInTitle.toLowerCase());

  }

  hasStatus(dad: Dad): boolean {
    if (this.dadSearchCriteria.status.length === 0) {
      return true;
    }
    return this.dadSearchCriteria.status.some(status => status.toLowerCase() === dad.status?.toLowerCase());
  }

  resetTable(): void {
    this.utilsService.saveUserData('dadAdminFilter', this.userChoicesFiltersDefaultDadAdmin);
    this.initializeDateFilter(this.userChoicesFiltersDefaultDadAdmin);
    this.exchangesNames.forEach(exchange => {
      this.dadSearchCriteria.exchanges[exchange] = false;
    });
    this.resetDadsWithDate();
    this.dadSearchCriteria = new DadSearchCriteria(this.userChoicesFiltersDefaultDadAdmin);
  }

  resetDadsWithDate() {
    this.getDadsList();
  }

  editBoxUrl(dad: Dad) {
    this.currentBoxDad = dad;
    this.currentBoxUrl = dad.boxUrl;
    this.boxUrlDialogVisible = true;
  }

  saveBoxUrl() {
    if (!this.currentBoxUrl?.startsWith('https://mckay-brothers.app.box.com')) {
      this.boxUrlError = true;
      return;
    }
    this.dadsService.editBoxUrl(this.currentBoxDad, this.currentBoxUrl).subscribe(() => {
      this.boxUrlDialogVisible = false;
      this.currentBoxDad.boxUrl = this.currentBoxUrl;
      this.boxUrlError = false;
      this.success = true;
      this.successMessage = 'Box URL edited successfully.';
      window.scrollTo({top: 0, left: 0, behavior: 'smooth'});
    });
  }

  closeBoxUrl() {
    this.boxUrlDialogVisible = false;
    this.boxUrlError = false;
  }

  removeError() {
    if (this.currentBoxUrl === '') {
      this.boxUrlError = false;
    }
  }

  protected getCutoffDate(dad: Dad): DateTime | null {
    const dadConfig = dad?.dadConfig;
    if (!dadConfig?.quincyCutoffStartDay || !dadConfig?.quincyCutoffStartTime) {
      return null;
    }

    const created = DateTime.fromISO(dad.createdAt, {zone: 'utc'});
    const monthYear = created.toFormat('MM/yyyy');

    const time = this.getTimeFromDateString(dadConfig.quincyCutoffStartTime);
    const day = String(dadConfig.quincyCutoffStartDay).padStart(2, '0');
    const zone = this.cleanZone(dadConfig.timezone);
    const localCutoff = DateTime.fromFormat(
      `${day}/${monthYear} ${time}`,
      'dd/MM/yyyy HH:mm',
      { zone: zone }
    );
    return localCutoff.toUTC();
  }

  private getTimeFromDateString(str: string): string | undefined {
    const m = str.match(/([Zz]|[+-]\d{2}:\d{2})$/);
    if (!m) {
      return formatDate(str, 'HH:mm', 'en-US');
    }
    const offsetFromIso = m[1].toUpperCase() === 'Z' ? '+0000' : m[1].replace(':', '');
    return formatDate(str, 'HH:mm', 'en-US', offsetFromIso);
  }

  private cleanZone(z?: string): string {
    return (z || 'UTC').replace(/[()]/g, '').trim();
  }

  statusClass(status: string): string {
    return 'status-pill--' + status.toLowerCase().replace(/ /g, '-');
  }
}
